import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Link from 'ui-library/Link';

import {navigateToStudyDesignTab} from '../../FileUtils';
import {ProceduresDropdownListingQuery_proceduresListing_ProcedureNode as ProcedureDropdownTemplate} from '../shared/__generated/ProceduresDropdownListingQuery';
import {PresentationalProcedureDropdown} from '../shared/ProcedureDropdown';

interface NoQueryProcedureDropdownProps {
  isDisabled: boolean;
  selectedProcedureId: string | null;
  onChangeSelectedProcedure: (procedureId: string | null) => void;
  onRequestCloseModal: () => void;
  placeholderText?: string;
  studyId: string;
  procedureTemplatesToRender: Array<ProcedureDropdownTemplate>;
}

export function PresentationalProcedureNoAvailableProceduresOnStudyDesignWarningBanner(
  props: NoQueryProcedureDropdownProps
): JSX.Element {
  return (
    <div className={css(styles.banner)}>
      <InlineBanner
        bannerType={InlineBannerType.WARNING}
        supportingInfo={
          <p className={css(styles.message)}>
            <strong>
              To add{' '}
              {getProcedureUIString(ProceduresStringKeys.PROCEDURE, ProcedureType.ASSAY, {
                stringCase: StringCase.LOWER,
                prependAorAn: true,
              })}{' '}
              run, add{' '}
              {getProcedureUIString(ProceduresStringKeys.PROCEDURE, ProcedureType.ASSAY, {
                stringCase: StringCase.LOWER,
                prependAorAn: true,
              })}{' '}
              to your{' '}
              <Link
                onClick={async () => {
                  assert(props.studyId !== undefined, 'studyId expected to be defined');
                  await navigateToStudyDesignTab(props.studyId);
                  props.onRequestCloseModal();
                }}
              >
                study design
              </Link>{' '}
              or contact your Benchling administrator for assistance.
            </strong>
          </p>
        }
      />
    </div>
  );
}

function NoQueryProcedureDropdown(props: NoQueryProcedureDropdownProps): JSX.Element {
  const sortedProcedures = props.procedureTemplatesToRender.sort(
    (a, b) =>
      a.templateCollection.name.localeCompare(b.templateCollection.name) || a.name.localeCompare(b.name)
  );
  return (
    <>
      <PresentationalProcedureDropdown
        {...{...props, procedureType: ProcedureType.ASSAY, procedures: sortedProcedures}}
        // We need the latest possible procedure version id because that gets stamped into the new run
        procedureFetchPolicy="network-only"
      />
      {sortedProcedures.length === 0 && (
        <PresentationalProcedureNoAvailableProceduresOnStudyDesignWarningBanner {...props} />
      )}
    </>
  );
}

interface AssayRunsTableProcedureDropdownProps {
  procedureId: string | null;
  setProcedureId: (procedureId: string) => void;
  onRequestCloseModal: () => void;
  requiredField: boolean;
  dropdownDisabled: boolean;
  studyId: string;
  procedureTemplatesToRender: Array<ProcedureDropdownTemplate>;
}

export default function AssayRunsTableProcedureDropdown(
  props: AssayRunsTableProcedureDropdownProps
): JSX.Element {
  return (
    <FormGroup>
      <FieldLabel
        label={getProcedureUIString(ProceduresStringKeys.PROCEDURE, ProcedureType.ASSAY, {
          stringCase: StringCase.SENTENCE,
        })}
        isRequired={props.requiredField}
      />
      <NoQueryProcedureDropdown
        isDisabled={props.dropdownDisabled}
        selectedProcedureId={props.procedureId}
        onChangeSelectedProcedure={props.setProcedureId}
        onRequestCloseModal={props.onRequestCloseModal}
        studyId={props.studyId}
        procedureTemplatesToRender={props.procedureTemplatesToRender}
      />
    </FormGroup>
  );
}

const styles = StyleSheet.create({
  banner: {
    marginTop: 10,
  },
  message: {
    marginBottom: 0,
  },
});
